import { CheckCircle, LocationOn } from "@mui/icons-material";
import { Avatar, Box, Typography, Paper, Divider, CircularProgress } from "@mui/material";
import useStore from "../../../store";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useEffect, useState } from "react";
import L from 'leaflet';

// Add custom CSS for the map popup
const customPopupStyle = `
  .custom-popup .leaflet-popup-content-wrapper {
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
  .custom-popup .leaflet-popup-content {
    margin: 0;
    padding: 0;
  }
  .custom-popup .leaflet-popup-tip {
    background: white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
`;

// Inject the custom styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.innerText = customPopupStyle;
  document.head.appendChild(styleSheet);
}

// Fix for the default marker icon in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function AppointmentConfirm() {
  const { departments, selectedDepartment, appointmentDetails, providers, selectedProvider } = useStore();
  const department = departments.filter((d) => d.departmentid === selectedDepartment);
  const provider = providers.find(p => p.providerid === selectedProvider);
  const [providerImage, setProviderImage] = useState(null);
  
  console.log("Department:", department);
  console.log("Appointment details:", appointmentDetails);
  console.log("Provider details:", provider);
  
  // Effect to load provider image when provider changes
  useEffect(() => {
    // Try to load the provider image if the provider is selected
    if (provider) {
      // Get provider name properly based on available fields
      const providerName =
        `${provider.firstname || ''} ${provider.lastname || ''}`;
      
      // Log provider object for debugging
      console.log("Provider object in AppointmentConfirm:", JSON.stringify(provider));
      
      // Safety check - don't proceed if name is empty
      if (!providerName.trim()) {
        console.warn("Provider name is empty, cannot load image");
        return;
      }
      
      console.log("Loading image for provider in AppointmentConfirm:", providerName);
      
      // Convert provider name to lowercase and replace spaces with dashes for filename
      const providerFileName = providerName.toLowerCase().replace(/\s+/g, '-');
      import(`../../../assets/images/providers/${providerFileName}.png`)
        .then(image => {
          console.log("Successfully loaded provider image for", providerName);
          setProviderImage(image.default);
        })
        .catch(err => {
          console.error("Could not load provider image:", err);
          setProviderImage(null);
        });
    } else {
      console.log("No provider data available in AppointmentConfirm");
    }
  }, [provider]);

  // Format the appointment date and time
  const formatAppointmentDateTime = () => {
    if (!appointmentDetails || !appointmentDetails.date || !appointmentDetails.time) {
      return "Appointment time not available";
    }
    
    try {
      // Parse the date (assuming format MM/DD/YYYY)
      const dateParts = appointmentDetails.date.split('/');
      if (dateParts.length !== 3) return "Invalid date format";
      
      const month = parseInt(dateParts[0]) - 1; // Months are 0-indexed in JS
      const day = parseInt(dateParts[1]);
      const year = parseInt(dateParts[2]);
      
      // Create a date object and format it
      const appointmentDate = new Date(year, month, day);
      const dayOfWeek = appointmentDate.toLocaleString('en-US', { weekday: 'long' });
      const monthName = appointmentDate.toLocaleString('en-US', { month: 'short' });
      
      return `${dayOfWeek}, ${monthName} ${day}, ${appointmentDetails.time}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Date formatting error";
    }
  };

  // Get the provider's full name with credentials
  const getProviderFullName = () => {
    if (!provider) return "Provider information not available";
    
    // Handle special providers like IV_OFFICE that use displayname instead of firstname/lastname
    if (provider.displayname) {
      // For IV_OFFICE provider, display a more user-friendly name
      if (provider.displayname === 'IV_OFFICE') {
        return "IV Office Service";
      } else if (provider.displayname === 'IV_HOME') {
        return "IV Home Service";
      }
      // For other providers with displayname
      return provider.displayname;
    }
    
    // For regular providers, use firstname and lastname
    let fullName = `${provider.firstname || ''} ${provider.lastname || ''}`;
    fullName = fullName.trim();
    
    // Add credentials if available
    if (provider.credentials) {
      fullName += `, ${provider.credentials}`;
    }
    
    return fullName || "Provider information not available";
  };

  // Get formatted address from department
  const getFormattedAddress = () => {
    if (!department || !department.length || !department[0].address) {
      return "Address information not available";
    }
    
    const { address, city, state, zip } = department[0];
    const addressParts = [];
    
    if (address) addressParts.push(address);
    if (city) addressParts.push(city);
    if (state) {
      if (zip) {
        addressParts.push(`${state} ${zip}`);
      } else {
        addressParts.push(state);
      }
    } else if (zip) {
      addressParts.push(zip);
    }
    
    return addressParts.join(', ');
  };

  return (
    <div>
      <Box sx={{ 
        display: "flex", 
        alignItems: "center", 
        gap: { xs: 1, sm: 2 }, 
        mb: 4,
        flexDirection: { xs: 'column', sm: 'row' },
        textAlign: { xs: 'center', sm: 'left' }
      }}>
        <Avatar
          src={providerImage || "/profile.jpg"}
          alt={provider ? (`${provider.firstname || ''} ${provider.lastname || ''}`) : 'Provider'}
          sx={{
            width: { xs: 60, sm: 80 },
            height: { xs: 60, sm: 80 },
            border: 2,
            borderColor: "#383352",
          }}
        />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 500, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {formatAppointmentDateTime()}
          </Typography>
          <Typography variant="button" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{getProviderFullName()}</Typography>
        </Box>
      </Box>

      <Typography variant="h5" sx={{ 
        mb: 3, 
        fontSize: { xs: '1.25rem', sm: '1.5rem' },
        textAlign: { xs: 'center', sm: 'left' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: { xs: 'center', sm: 'flex-start' },
        gap: 1
      }}>
        <CheckCircle className="text-[#4C9D3A] !w-6 !h-6 sm:!w-8 sm:!h-8" /> You're all set!
      </Typography>
      
      {department.length > 0 && (
        <Paper elevation={1} sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
            <LocationOn color="primary" sx={{ mr: 1 }} />
            Office Location
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, fontWeight: 'bold' }}>
            {department[0].name || "Medical Office"}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {getFormattedAddress()}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          { <MapWrapper department={department[0]} />}
        </Paper>
      )}
    </div>
  );
}

export default AppointmentConfirm;

const MapUpdater = ({ coords }) => {
  const map = useMap();
  useEffect(() => {
    // Ensure the marker is centered in the viewport with appropriate zoom
    map.setView(coords, 16, { 
      animate: true,
      duration: 1.0,
      easeLinearity: 0.1
    });
    
    // Additional check to ensure marker is visible
    setTimeout(() => {
      if (map.getZoom() < 14) {
        map.setView(coords, 16, { animate: true });
      }
    }, 100);
  }, [coords, map]);
  return null;
};


const MapWrapper = ({ department }) => {
  const { latitude, longitude, address, name, city, state, zip } = department;
  const [coords, setCoords] = useState([34.1522744, -118.3411072]); // Default to Burbank area
  const [locationLoaded, setLocationLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCoordinates = async () => {
    setLoading(true);
    setError(null);
    try {
      // Clean and format address for better geocoding results
      const cleanAddress = address?.replace(/\s+STE\s+\d+/i, '').trim(); // Remove suite numbers
      const cleanZip = zip?.split('-')[0]; // Remove zip+4 extension
      
      // Try multiple address formats for better geocoding success
      const addressVariations = [
        // Full address with suite
        [address, city, state, zip].filter(Boolean).join(', '),
        // Address without suite
        [cleanAddress, city, state, cleanZip].filter(Boolean).join(', '),
        // Just street address and city
        [cleanAddress, city, state].filter(Boolean).join(', '),
        // Street address with zip
        [cleanAddress, cleanZip].filter(Boolean).join(', ')
      ];
      
      console.log("Trying address variations:", addressVariations);
      
      let geocodingSuccess = false;
      
      for (const addressToTry of addressVariations) {
        if (!addressToTry.trim()) continue;
        
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addressToTry)}&limit=1&addressdetails=1&extratags=1&countrycodes=us`
          );
          const data = await response.json();
          console.log(`Geocoding attempt for "${addressToTry}":`, data);
          
          if (data.length > 0) {
            const result = data[0];
            setCoords([parseFloat(result.lat), parseFloat(result.lon)]);
            setLocationLoaded(true);
            console.log("Successfully geocoded address:", addressToTry, "to coordinates:", [result.lat, result.lon]);
            geocodingSuccess = true;
            break;
          }
        } catch (err) {
          console.warn(`Geocoding failed for "${addressToTry}":`, err);
          continue;
        }
      }
      
      if (!geocodingSuccess) {
        console.warn("No coordinates found for any address variation");
        setError("Location not found - using approximate location");
        // Use the coordinates from the Google Maps link you provided for Burbank
        setCoords([34.1522744, -118.3411072]);
        setLocationLoaded(true);
      }
    } catch (error) {
      console.error("Error geocoding address:", error);
      setError("Failed to load location - using approximate location");
      // Fallback to Burbank coordinates from your Google Maps link
      setCoords([34.1522744, -118.3411072]);
      setLocationLoaded(true);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (latitude && longitude) {
      console.log("Using provided coordinates:", latitude, longitude);
      setCoords([parseFloat(latitude), parseFloat(longitude)]);
      setLocationLoaded(true);
    } else if (city) {
      const cityUpper = city.toUpperCase();
      console.log("Checking city for predefined coordinates:", cityUpper);
      
      // Use exact coordinates for known cities
      if (cityUpper === 'BURBANK') {
        console.log("Using Burbank coordinates for city:", city);
        setCoords([34.1522744, -118.3411072]);
        setLocationLoaded(true);
      } else if (cityUpper === 'GLENDALE') {
        console.log("Using Glendale coordinates for city:", city);
        setCoords([34.1425, -118.2551]);
        setLocationLoaded(true);
      } else if (cityUpper === 'LA CANADA' || cityUpper === 'LA CANADA FLINTRIDGE') {
        console.log("Using La Canada Flintridge coordinates for city:", city);
        setCoords([34.2085553, -118.2085313]);
        setLocationLoaded(true);
      } else if (address) {
        console.log("Fetching coordinates for address:", address);
        fetchCoordinates();
      }
    } else if (address) {
      console.log("Fetching coordinates for address:", address);
      fetchCoordinates();
    }
  }, [department]);

  // Format full address for popup
  const getFullAddress = () => {
    const addressParts = [];
    if (address) addressParts.push(address);
    if (city) addressParts.push(city);
    if (state) {
      if (zip) {
        addressParts.push(`${state} ${zip}`);
      } else {
        addressParts.push(state);
      }
    } else if (zip) {
      addressParts.push(zip);
    }
    
    return addressParts.join(', ');
  };

  // Create a custom marker icon
  const createCustomIcon = () => {
    return L.divIcon({
      className: 'custom-marker',
      html: `
        <div style="
          background: #4C9D3A;
          width: 30px;
          height: 30px;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          border: 3px solid white;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <div style="
            transform: rotate(45deg);
            color: white;
            font-size: 16px;
            font-weight: bold;
            margin-top: -2px;
          ">📍</div>
        </div>
      `,
      iconSize: [30, 30],
      iconAnchor: [15, 30],
      popupAnchor: [0, -30]
    });
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {loading && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255,255,255,0.8)',
          zIndex: 1000,
          borderRadius: '8px'
        }}>
          <CircularProgress size={40} />
        </Box>
      )}
      
      {error && (
        <Box sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          right: 8,
          backgroundColor: 'rgba(255,193,7,0.9)',
          color: 'black',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          zIndex: 1001,
          textAlign: 'center'
        }}>
          ⚠️ {error}
        </Box>
      )}
      
      <MapContainer 
        style={{ 
          height: window.innerWidth <= 768 ? "250px" : "300px", 
          width: "100%", 
          borderRadius: "8px",
          border: "2px solid #e0e0e0",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
        }} 
        center={coords} 
        zoom={16} 
        zoomControl={true} 
        scrollWheelZoom={true}
        doubleClickZoom={true}
        dragging={true}
        touchZoom={true}
        boxZoom={true}
        keyboard={true}
        whenReady={() => {
          // Ensure marker is visible when map is ready
          console.log("Map is ready, ensuring marker visibility");
        }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {locationLoaded && (
          <Marker 
            position={coords} 
            icon={createCustomIcon()}
            eventHandlers={{
              add: (e) => {
                // Ensure marker is in viewport when added
                const map = e.target._map;
                if (map) {
                  setTimeout(() => {
                    map.setView(coords, 16, { animate: true });
                  }, 100);
                }
              }
            }}
          >
            <Popup maxWidth={250} className="custom-popup">
              <Box 
                sx={{ 
                  p: 1, 
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#f5f5f5'
                  }
                }}
                onClick={() => {
                  const fullAddress = getFullAddress();
                  const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`;
                  window.open(googleMapsUrl, '_blank');
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#4C9D3A' }}>
                  {name || "Medical Office"}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.4 }}>
                  {getFullAddress()}
                </Typography>
                <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #eee' }}>
                  <Typography variant="caption" sx={{ color: '#4C9D3A', fontWeight: 'medium' }}>
                    🗺️ Click to get directions
                  </Typography>
                </Box>
              </Box>
            </Popup>
          </Marker>
        )}
        <MapUpdater coords={coords} />
      </MapContainer>
    </Box>
  );
};
